// Data structure › Enums (spec/dashboard.md section 5.4): every enum of the branch, own and system, with the number of
// parameters that name it (computed from the templates).
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { EnumDefinition, Template } from '../../api/types';
import { isSystemTemplate, routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { DataGrid } from '../../ui/DataGrid';
import { AlertCircle, ListTree, Plus, Search } from '../../ui/icons';
import { Dot, SchemaTabs, useSchemaEnv } from './common';
import './schema.css';

export interface EnumUse {
  template: Template;
  parameter: string;
  /** the parameter is a list whose elements are the enum */
  list: boolean;
}

/** The parameters that name an enum: each declared parameter once (own parameters, so an inherited one is not counted per heir). */
export function enumUsers(templates: Template[], name: string): EnumUse[] {
  const out: EnumUse[] = [];
  for (const t of templates) {
    for (const p of t.parameters) {
      if (p.enum === name) out.push({ template: t, parameter: p.name, list: false });
      else if (p.of?.enum === name) out.push({ template: t, parameter: p.name, list: true });
    }
  }
  return out;
}

export function EnumsPage() {
  const { catalog, org, gameSlug, branch, navigate, canWrite } = useSchemaEnv();
  const [search, setSearch] = useState('');
  const usage = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of catalog.enums.keys()) m.set(e, enumUsers(catalog.templateList, e).length);
    return m;
  }, [catalog.enums, catalog.templateList]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = Array.from(catalog.enums.values()).sort((a, b) => a.name.localeCompare(b.name));
    return q ? all.filter((e) => e.name.toLowerCase().includes(q) || (e.displayName ?? '').toLowerCase().includes(q)) : all;
  }, [catalog.enums, search]);

  return (
    <section className="schema-page">
      <div className="page-head">
        <h1>Data structure</h1>
        {!catalog.loading && <span className="page-meta">{catalog.enums.size} enum{catalog.enums.size === 1 ? '' : 's'}</span>}
        <span className="spacer" />
        <Button variant="primary" disabled={!canWrite} onClick={() => navigate(routes.enumNew(org, gameSlug, branch))} icon={<Plus aria-hidden="true" />}>
          New enum
        </Button>
      </div>
      <SchemaTabs />
      <DataGrid<EnumDefinition>
        id="enums"
        rows={rows}
        rowKey={(e) => e.name}
        loading={catalog.loading}
        caption={`${rows.length} enum${rows.length === 1 ? '' : 's'}${search ? ' (filtered client-side)' : ''}`}
        search={{ value: search, onSearch: setSearch, placeholder: 'Search enums…' }}
        onRowOpen={(e) => navigate(routes.enumEditor(org, gameSlug, branch, e.name))}
        emptyState={
          catalog.error ? (
            <EmptyState icon={<AlertCircle />} title="The enums did not load" body={catalog.error} />
          ) : search ? (
            <EmptyState icon={<Search />} title="No enum matches" body="Change the search to see more enums." />
          ) : (
            <EmptyState
              icon={<ListTree />}
              title="No enums on this branch yet"
              body="An enum is a named list of values a parameter can take."
              action={canWrite ? <Button size="s" icon={<Plus aria-hidden="true" />} onClick={() => navigate(routes.enumNew(org, gameSlug, branch))}>Create an enum</Button> : undefined}
            />
          )
        }
        columns={[
          { key: 'name', header: 'Name', fixed: true, render: (e) => <Link to={routes.enumEditor(org, gameSlug, branch, e.name)}>{e.name}</Link>, cellText: (e) => e.name, sortValue: (e) => e.name },
          { key: 'displayName', header: 'Display name', render: (e) => e.displayName ?? '', sortValue: (e) => e.displayName ?? '' },
          { key: 'system', header: 'Origin', render: (e) => (isSystemTemplate(e.name) ? <Badge tone="info">System</Badge> : <Badge>Own</Badge>), sortValue: (e) => (isSystemTemplate(e.name) ? 1 : 0) },
          { key: 'isFlags', header: 'Flags enum', render: (e) => (
              <span className="dot-word">
                <Dot on={e.isFlags} label="Flags enum" />
                {e.isFlags ? 'Yes' : 'No'}
              </span>
            ),
            sortValue: (e) => (e.isFlags ? 1 : 0) },
          { key: 'values', header: 'Values', align: 'right', render: (e) => e.values.length, sortValue: (e) => e.values.length },
          { key: 'usedBy', header: 'Used by', align: 'right', render: (e) => usage.get(e.name) ?? 0, sortValue: (e) => usage.get(e.name) ?? 0 },
          { key: 'description', header: 'Description', render: (e) => e.description ?? '', cellText: (e) => e.description ?? '' },
        ]}
      />
    </section>
  );
}
